import React, { useEffect, useState, useCallback } from 'react';
import { Activity, RefreshCw, AlertCircle, CheckCircle, Database, Cpu } from 'lucide-react';
import apiClient from '../../utils/api';

export const SuperAdminSystemHealth: React.FC = () => {
  const [health, setHealth] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [logType, setLogType] = useState<'error' | 'combined'>('error');
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getSystemHealth();
      setHealth(res.data.data);
    } catch {} finally { setLoading(false); }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await apiClient.getSystemLogs(logType, 100);
      setLogs(res.data.data?.lines ?? []);
    } catch {} finally { setLogsLoading(false); }
  }, [logType]);

  useEffect(() => { loadHealth(); loadLogs(); }, [loadHealth, loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadHealth, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadHealth]);

  const isHealthy = health?.status === 'healthy';

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={20} /> System Health
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (30s)
          </label>
          <button className="btn btn-ghost" onClick={() => { loadHealth(); loadLogs(); }} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading ? <div className="loading-spinner">Loading system health...</div> : (
        <>
          {/* Status banner */}
          <div className="card" style={{ marginBottom: 20, background: isHealthy ? '#f0fdf4' : '#fef2f2', border: `1px solid ${isHealthy ? '#86efac' : '#fca5a5'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isHealthy
                ? <CheckCircle size={24} color="#22c55e" />
                : <AlertCircle size={24} color="#ef4444" />}
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: isHealthy ? '#15803d' : '#dc2626' }}>
                  {isHealthy ? 'All Systems Operational' : 'System Degraded'}
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>Last checked: {health?.timestamp ? new Date(health.timestamp).toLocaleString() : '—'}</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
                Uptime: <strong>{health?.uptime?.human || '—'}</strong>
              </div>
            </div>
          </div>

          {/* Metric cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
            {[
              { label: 'DB Status', value: health?.database?.status?.toUpperCase() || '—', sub: `${health?.database?.latencyMs ?? '—'}ms latency`, color: health?.database?.status === 'ok' ? '#22c55e' : '#ef4444', icon: <Database size={18} /> },
              { label: 'Heap Used', value: `${health?.memory?.heapUsedMB ?? '—'} MB`, sub: `of ${health?.memory?.heapTotalMB ?? '—'} MB total`, color: '#3b82f6', icon: <Cpu size={18} /> },
              { label: 'RSS Memory', value: `${health?.memory?.rssMB ?? '—'} MB`, sub: 'resident set size', color: '#6366f1', icon: <Cpu size={18} /> },
              { label: 'Response Time', value: `${health?.responseTimeMs ?? '—'}ms`, sub: 'health check latency', color: '#f59e0b', icon: <Activity size={18} /> },
            ].map(c => (
              <div className="stat-card" key={c.label} style={{ borderLeft: `4px solid ${c.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div className="stat-label">{c.label}</div>
                    <div className="stat-value" style={{ color: c.color, fontSize: 22 }}>{c.value}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{c.sub}</div>
                  </div>
                  <div style={{ color: c.color, opacity: 0.5 }}>{c.icon}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Log viewer */}
          <div className="card">
            <div className="page-header" style={{ marginBottom: 12 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600 }}>Recent Logs</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={logType} onChange={e => setLogType(e.target.value as any)} style={{ width: 160 }}>
                  <option value="error">Error Log</option>
                  <option value="combined">Combined Log</option>
                </select>
                <button className="btn btn-ghost" onClick={loadLogs} disabled={logsLoading}>
                  <RefreshCw size={13} />
                </button>
              </div>
            </div>
            {logsLoading ? <div className="loading-spinner">Loading logs...</div> : (
              <div style={{
                background: '#0f172a', borderRadius: 8, padding: 16, maxHeight: 400,
                overflowY: 'auto', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0',
              }}>
                {logs.length === 0 ? (
                  <span style={{ color: '#64748b' }}>No log entries found.</span>
                ) : logs.map((line, i) => {
                  const isError = line.toLowerCase().includes('error') || line.toLowerCase().includes('warn');
                  return (
                    <div key={i} style={{ color: isError ? '#fca5a5' : '#94a3b8', marginBottom: 2, wordBreak: 'break-all' }}>
                      {line}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
